import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../services/api';
import { Plus, Edit2, Trash2, Package, X, Check } from 'lucide-react';
import { toast } from '../../components/common/Toast';

const PACK_TYPES = [
  'NEC', 'BEB', 'CON', 'LIC', 'MAR', 'SNK', 'BAB', 'MASC', 'ASEO_DAMA', 'ASEO_CAB', 'ASEO_NIN', 'VEG', 'GFREE', 'LAV', 'COC',
];

const AdminPacks = () => {
  const { isDarkMode } = useTheme();
  const [packs, setPacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', pack_type: 'NEC', slug: '', description: '', price: '', vu: '', commission_rate: '',
    sku: '', is_active: true, pack_items: [],
  });

  const load = async () => {
    try {
      const [packsRes, prodRes] = await Promise.all([
        adminAPI.getPacks({ active_only: false }),
        adminAPI.getProducts({ limit: 200 }),
      ]);
      setPacks(packsRes.data);
      setProducts(prodRes.data?.products || prodRes.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', pack_type: 'NEC', slug: '', description: '', price: '', vu: '', commission_rate: '', sku: '', is_active: true, pack_items: [] });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, pack_type: p.pack_type, slug: p.slug, description: p.description || '',
      price: p.price, vu: p.vu, commission_rate: p.commission_rate, sku: p.sku,
      is_active: p.is_active, pack_items: (p.items || []).map(i => ({ product_id: i.product_id, quantity: i.quantity })),
    });
    setShowModal(true);
  };

  const addItem = () => setForm({ ...form, pack_items: [...form.pack_items, { product_id: products[0]?.id || '', quantity: 1 }] });
  const removeItem = (idx) => setForm({ ...form, pack_items: form.pack_items.filter((_, i) => i !== idx) });

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.warning('Nombre y precio requeridos');
    setSaving(true);
    try {
      const payload = {
        ...form, price: parseFloat(form.price), vu: parseFloat(form.vu) || 0,
        commission_rate: parseFloat(form.commission_rate) || 0,
        sku: form.sku || undefined,
      };
      if (editing) {
        await adminAPI.updatePack(editing.id, payload);
        toast.success('Pack actualizado');
      } else {
        await adminAPI.createPack(payload);
        toast.success('Pack creado');
      }
      setShowModal(false);
      load();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este pack?')) return;
    try { await adminAPI.deletePack(id); toast.success('Pack eliminado'); load(); }
    catch { toast.error('Error al eliminar'); }
  };

  const getProdName = (id) => products.find(p => p.id === id)?.name || '—';
  const packTypeLabel = (t) => ({ NEC: 'Primera Necesidad', BEB: 'Bebé', LIC: 'Licor', MAR: 'Marca Sttora', SNK: 'Snacks', MASC: 'Mascotas', CON: 'Condimentos', ASED: 'Aseo Dama', ASEC: 'Aseo Caballero', ASEN: 'Aseo Niños', VEG: 'Vegano', GFR: 'Gluten Free', LAV: 'Lavandería', COC: 'Cocina' }[t] || t);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" /></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Sttora Packs</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{packs.length} packs registrados</p>
        </div>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl text-sm font-medium shadow-lg shadow-orange-500/20"
        ><Plus className="w-4 h-4" /> Nuevo Pack</motion.button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {packs.map(p => (
          <motion.div key={p.id} whileHover={{ y: -4 }}
            className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all p-6`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{p.name}</h3>
                  <p className="text-xs text-gray-400">{p.sku}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${p.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700'}`}>
                {p.is_active ? 'Activo' : 'Inactivo'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-orange-600 dark:text-orange-400 font-bold text-lg">${p.price?.toFixed(2)}</span>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{packTypeLabel(p.pack_type)}</span>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {(p.items || []).map((item, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs">{item.product_name || getProdName(item.product_id)} x{item.quantity}</span>
              ))}
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button onClick={() => openEdit(p)} className="flex-1 px-3 py-2 border border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400 rounded-2xl text-sm hover:bg-orange-50 dark:hover:bg-orange-900/20"><Edit2 className="w-3.5 h-3.5 inline mr-1" /> Editar</button>
              <button onClick={() => handleDelete(p.id)} className="px-3 py-2 border border-red-200 dark:border-red-800 text-red-500 rounded-2xl text-sm hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="w-3.5 h-3.5 inline" /></button>
            </div>
          </motion.div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8" onClick={() => setShowModal(false)}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl p-8 w-full max-w-2xl mx-4 shadow-2xl`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{editing ? 'Editar' : 'Nuevo'} Pack</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nombre</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tipo de Pack</label>
                <select value={form.pack_type} onChange={e => setForm({...form, pack_type: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}>
                  {PACK_TYPES.map(t => <option key={t} value={t}>{t} - {packTypeLabel(t)}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Slug</label>
                <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
              </div>
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Descripción</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Precio ($)</label>
                <input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>VU</label>
                <input type="number" value={form.vu} onChange={e => setForm({...form, vu: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Comisión (%)</label>
                <input type="number" value={form.commission_rate} onChange={e => setForm({...form, commission_rate: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>SKU (opcional)</label>
                <input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}
                  placeholder="STP-NEC-000001-FAM" />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Activo</label>
                <select value={form.is_active} onChange={e => setForm({...form, is_active: e.target.value === 'true'})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}>
                  <option value={true}>Sí</option>
                  <option value={false}>No</option>
                </select>
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Productos del Pack</label>
                  <motion.button whileHover={{ scale: 1.02 }} onClick={addItem}
                    className="text-xs px-3 py-1.5 bg-orange-500 text-white rounded-xl">+ Agregar Producto</motion.button>
                </div>
                <div className="space-y-2">
                  {form.pack_items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <select value={item.product_id} onChange={e => {
                        const items = [...form.pack_items];
                        items[idx] = { ...items[idx], product_id: parseInt(e.target.value) };
                        setForm({...form, pack_items: items});
                      }} className={`flex-1 px-3 py-2 rounded-xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <input type="number" min={1} value={item.quantity} onChange={e => {
                        const items = [...form.pack_items];
                        items[idx] = { ...items[idx], quantity: parseInt(e.target.value) || 1 };
                        setForm({...form, pack_items: items});
                      }} className="w-20 px-3 py-2 rounded-xl border text-sm text-center" />
                      <button onClick={() => removeItem(idx)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl"><X className="w-4 h-4 text-red-500" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={handleSave} disabled={saving}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl text-sm font-medium disabled:opacity-50"
              >{saving ? 'Guardando...' : 'Guardar'}</motion.button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-2xl text-sm">Cancelar</button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminPacks;
